// Text catalog for the Datacentre / Data centers industry page.
// Icons, images and link targets live in src/pages/DatacentrePage.tsx and are
// aligned by index with the arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type Card = { title: string; text: string };

type DatacentreStrings = {
  meta: PageMeta;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  jsonLd: { serviceType: string; name: string; description: string; areaServed: string; audienceType: string };
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; p1: string; p2: string };
  expertiseSection: { eyebrow: string; heading: string };
  expertise: Card[];
  servicesSection: { eyebrow: string; heading: string; intro: string; readMore: string };
  services: Card[];
  projectTypesSection: { eyebrow: string; heading: string; intro: string };
  projectTypes: string[];
  why: { eyebrow: string; heading: string; cards: Card[] };
  cta: { heading: string; body: string; cta: string };
};

export const datacentreStrings: Record<Locale, DatacentreStrings> = {
  da: {
    meta: {
      title: "Bygherrerådgivning til datacentre | MI Support",
      description:
        "Specialiseret bygge- og projektledelse til datacentre og store digitale anlæg. Vi sikrer driftssikkerhed og koordinerer el, køling og teknik.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    keywords:
      "datacenter byggeri, hyperscale, mission critical byggeri, Tier III, Tier IV, datacenter projektledelse, MEP coordination, Cx commissioning, Level 5 commissioning, white space, grey space, UPS, dieselgenerator, kølingsanlæg, datacenter Danmark, edge datacenter",
    ogTitle: "Datacentre & Hyperscale | MI Support",
    ogDescription:
      "Erfaren bygherrerådgivning og projektledelse til driftskritisk datacenterbyggeri, fra design og udførelse til commissioning og overdragelse.",
    jsonLd: {
      serviceType: "Bygherrerådgivning og projektledelse til datacenterbyggeri",
      name: "Datacentre & Hyperscale byggeri",
      description:
        "Specialiseret bygge-, projekt- og HSE-ledelse til driftskritisk datacentre, hyperscale-byggerier og colocation-faciliteter.",
      areaServed: "Danmark",
      audienceType: "Hyperscalers, colocation, edge providers, enterprise IT",
    },
    hero: {
      eyebrow: "Branche · Datacentre",
      title: "Bygherrerådgivning til datacentre",
      body: "Med MI Support som rådgiver får I en erfaren samarbejdspartner gennem hele datacenterprojektet. Vi tager ansvar for, at strøm, køling og teknik spiller sammen, at anlægget er gennemtestet inden idriftsættelse, og at driften kan overtages med ro i maven.",
      ctaPrimary: "Tag en uforpligtende snak",
      ctaSecondary: "Se alle brancher",
    },
    intro: {
      eyebrow: "Datacenterbyggeri kræver specialister",
      heading: "Når driftsstabilitet, teknik og test definerer projektet",
      p1: "I et datacenter er driftsstabilitet selve forretningsmodellen. De tekniske installationer såsom strømforsyning, køling, netværk og styresystemer udgør størstedelen af budgettet, og de skal ikke bare installeres, men også testes grundigt i samspil, før driften overtager. Med en projektleder fra MI Support får I et fast greb om netop den koordinering, så I kan træffe beslutningerne på et solidt grundlag.",
      p2: "Vores konsulenter har gennem flere år arbejdet med datacenterprojekter. Det giver os en specialiseret viden om, hvad der skal til, når teknikere, entreprenører og driftsfolk skal trække i samme retning under tidspres.",
    },
    expertiseSection: { eyebrow: "Vores ekspertise", heading: "Specialiseret viden om datacenterbyggeri" },
    expertise: [
      { title: "Driftssikre byggerier", text: "Vi bygger, så det virker. Vi fokuserer på backup-løsninger, fejlsikring og stabil drift både under og efter byggeriet." },
      { title: "Strøm & nødstrøm", text: "El-installationer, nødstrømsanlæg, generatorer og transformatorer, koordineret med energiselskaber og myndigheder." },
      { title: "Køling & ventilation", text: "Vi sørger for det rigtige kølesystem, så strømforbruget holdes nede og anlægget kører optimalt i det danske klima." },
      { title: "Grundig test & kontrol", text: "Vi tester alle systemer grundigt undervejs og ved overdragelsen, fra enkelte maskiner til hele anlæggets samlede drift." },
      { title: "Koordinering af teknik", text: "Tæt samarbejde mellem el, køling, styring og IT. Vi bruger digitale modeller til at undgå fejl og følge installationerne." },
      { title: "Højeste driftsklasser", text: "Erfaring med de strengeste krav til driftsikkerhed både fra internationale standarder og store globale tech-virksomheder." },
    ],
    servicesSection: {
      eyebrow: "Vores ydelser til datacentre",
      heading: "Det leverer vi til datacenter-bygherrer",
      intro:
        "Uanset om I bygger nyt fra bunden, udvider kapaciteten eller opgraderer et kørende anlæg, står vores konsulenter klar til at lede og koordinere projektet i samarbejde med jer.",
      readMore: "Læs mere",
    },
    services: [
      { title: "Bygherrerådgivning", text: "Uafhængig rådgiver gennem hele projektet, fra valg af grund og design til test, overdragelse og drift." },
      { title: "Projektledelse", text: "Erfarne projektledere, der sørger for, at byggeriet bliver færdigt til tiden, inden for budgettet og med den rette kvalitet." },
      { title: "Byggeledelse", text: "Byggeledere, der forstår teknisk tunge projekter, hurtige tidsplaner og samspillet mellem bygning og maskiner." },
      { title: "Tidsplanlægning", text: "Overordnede tidsplaner, hvor prøvekørsel, leverancer og tilslutning af strøm er indbygget i den kritiske fremdrift." },
      { title: "Projektøkonomi", text: "Oversigt over omkostninger og budget på projekter med meget avanceret udstyr og ofte skiftende krav." },
      { title: "Projektassistance", text: "Styring af dokumenter og koordinering af de mange leverandører, entreprenører og samarbejdspartnere." },
      { title: "Shutdown-planlægning", text: "Planlægning af tilslutning og opdateringer på kørende datacentre med absolut fokus på at undgå driftsstop." },
      { title: "Sikkerhedsledelse (HSE)", text: "Sikkerhedsarbejde på datacenterbyggepladser med mange el-installationer, kraner og overlapninger i arbejdet." },
    ],
    projectTypesSection: {
      eyebrow: "Anlægstyper",
      heading: "Erfaring fra hele datacenter-branchen",
      intro:
        "Vi samarbejder med både tech-virksomheder, udlejere og virksomheder med egne serverkapaciteter, og vi kender behovene fra begge sider af bordet.",
    },
    projectTypes: [
      "Virksomheders egne serverrum",
      "Mindre edge-datacentre",
      "Serverhaller og udstyrsrum",
      "Nødstrøms- og generatoranlæg",
      "Køle- og ventilationsanlæg",
      "Udvidelser og opgraderinger",
    ],
    why: {
      eyebrow: "Hvorfor MI Support",
      heading: "En partner der forstår, hvad der kræves",
      cards: [
        { title: "Specialiserede konsulenter", text: "I får adgang til konsulenter, der kommer fra både byggebranchen og fra teknik- og driftsafdelinger hos store internationale virksomheder. Det betyder, at vi kan tale samme sprog som både håndværkere og IT-specialister og holde alle parter samlet om projektet." },
        { title: "Uafhængig bygherrerådgiver", text: "Vi varetager udelukkende bygherres interesser. Uden bindinger til entreprenører eller leverandører kan vi stille de kritiske spørgsmål, når det gælder, og sikre jer fuld kontrol over projektet hele vejen." },
        { title: "Tests, der holder", text: "Hos os er tests og commissioning ikke en afslutning, men en rød tråd gennem hele projektet. Vi bygger strukturen op fra start, så I kan overdrage anlægget til driften planmæssigt og uden ubehagelige overraskelser." },
      ],
    },
    cta: {
      heading: "Skal vi hjælpe med jeres datacenterprojekt?",
      body: "Står I overfor et datacenterprojekt, hvor driftsstabilitet, teknik og tid skal gå hånd i hånd? Vi tager gerne en uforpligtende snak om jeres projekt og behov.",
      cta: "Kontakt os og hør mere",
    },
  },

  en: {
    meta: {
      title: "Client consultancy for data centers | MI Support",
      description:
        "Specialised construction and project management for data centers. We ensure operational reliability and coordinate power, cooling and engineering.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    keywords:
      "data center construction, hyperscale, mission critical construction, Tier III, Tier IV, data center project management, MEP coordination, Cx commissioning, Level 5 commissioning, white space, grey space, UPS, diesel generator, cooling plant, data center Denmark, edge data center",
    ogTitle: "Data centers & hyperscale | MI Support",
    ogDescription:
      "Experienced client consultancy and project management for mission-critical data center construction, from design and execution to commissioning and handover.",
    jsonLd: {
      serviceType: "Client consultancy and project management for data center construction",
      name: "Data center & hyperscale construction",
      description:
        "Specialised construction, project and HSE management for mission-critical data centers, hyperscale builds and colocation facilities.",
      areaServed: "Denmark",
      audienceType: "Hyperscalers, colocation, edge providers, enterprise IT",
    },
    hero: {
      eyebrow: "Industry · Data centers",
      title: "Client consultancy for data centers",
      body: "With MI Support as your advisor, you get an experienced partner throughout the entire data center project. We take responsibility for ensuring power, cooling and engineering work together, that the facility is fully tested before commissioning, and that operations can be taken over with peace of mind.",
      ctaPrimary: "Have a no-obligation chat",
      ctaSecondary: "See all industries",
    },
    intro: {
      eyebrow: "Data center construction requires specialists",
      heading: "When operational stability, engineering and testing define the project",
      p1: "In a data center, operational stability is the business model itself. The technical installations — power supply, cooling, networking and control systems — make up the majority of the budget, and they must not only be installed but also thoroughly tested in interaction before operations take over. With a project manager from MI Support, you get a firm grip on exactly that coordination, so you can make decisions on a solid basis.",
      p2: "Our consultants have worked with data center projects for several years. This gives us specialised knowledge of what it takes when engineers, contractors and operations staff need to pull in the same direction under time pressure.",
    },
    expertiseSection: { eyebrow: "Our expertise", heading: "Specialised knowledge of data center construction" },
    expertise: [
      { title: "Operationally reliable builds", text: "We build so it works. We focus on backup solutions, fail-safes and stable operation both during and after construction." },
      { title: "Power & backup power", text: "Electrical installations, backup power systems, generators and transformers, coordinated with energy companies and authorities." },
      { title: "Cooling & ventilation", text: "We ensure the right cooling system so power consumption is kept down and the facility runs optimally in the Danish climate." },
      { title: "Thorough testing & control", text: "We test all systems thoroughly along the way and at handover, from individual machines to the entire facility's combined operation." },
      { title: "Engineering coordination", text: "Close collaboration between power, cooling, controls and IT. We use digital models to avoid errors and track the installations." },
      { title: "Highest operational classes", text: "Experience with the strictest operational reliability requirements, both from international standards and large global tech companies." },
    ],
    servicesSection: {
      eyebrow: "Our services for data centers",
      heading: "This is what we deliver to data center clients",
      intro:
        "Whether you're building new from the ground up, expanding capacity or upgrading a running facility, our consultants are ready to lead and coordinate the project together with you.",
      readMore: "Read more",
    },
    services: [
      { title: "Client consultancy", text: "Independent advisor throughout the project, from site selection and design to testing, handover and operation." },
      { title: "Project management", text: "Experienced project managers who ensure the build is finished on time, within budget and with the right quality." },
      { title: "Construction management", text: "Construction managers who understand technically heavy projects, fast schedules and the interplay between building and machinery." },
      { title: "Scheduling", text: "Master schedules where test runs, deliveries and power connection are built into the critical path." },
      { title: "Project economics", text: "An overview of costs and budget on projects with highly advanced equipment and often changing requirements." },
      { title: "Project assistance", text: "Management of documents and coordination of the many suppliers, contractors and partners." },
      { title: "Shutdown planning", text: "Planning of connections and updates on running data centers with an absolute focus on avoiding downtime." },
      { title: "Safety management (HSE)", text: "Safety work on data center construction sites with many electrical installations, cranes and overlapping work." },
    ],
    projectTypesSection: {
      eyebrow: "Facility types",
      heading: "Experience from across the data center industry",
      intro:
        "We work with tech companies, landlords and companies with their own server capacity alike, and we know the needs from both sides of the table.",
    },
    projectTypes: [
      "Companies' own server rooms",
      "Smaller edge data centers",
      "Server halls and equipment rooms",
      "Backup power and generator systems",
      "Cooling and ventilation systems",
      "Expansions and upgrades",
    ],
    why: {
      eyebrow: "Why MI Support",
      heading: "A partner that understands what's required",
      cards: [
        { title: "Specialised consultants", text: "You get access to consultants who come from both the construction industry and the engineering and operations departments of large international companies. That means we can speak the same language as both tradespeople and IT specialists and keep all parties aligned around the project." },
        { title: "Independent client advisor", text: "We exclusively safeguard the client's interests. Without ties to contractors or suppliers, we can ask the critical questions when it matters and ensure you have full control over the project all the way." },
        { title: "Tests that hold up", text: "For us, testing and commissioning are not an endpoint but a common thread throughout the entire project. We build the structure from the start so you can hand the facility over to operations on schedule and without unpleasant surprises." },
      ],
    },
    cta: {
      heading: "Shall we help with your data center project?",
      body: "Are you facing a data center project where operational stability, engineering and time must go hand in hand? We're happy to have a no-obligation chat about your project and needs.",
      cta: "Contact us to learn more",
    },
  },

  de: {
    meta: {
      title: "Bauherrenberatung für Rechenzentren | MI Support",
      description:
        "Spezialisiertes Bau- und Projektmanagement für Rechenzentren. Wir sichern die Betriebssicherheit und koordinieren Strom, Kühlung und Technik.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    keywords:
      "Rechenzentrumsbau, Hyperscale, Mission-Critical-Bau, Tier III, Tier IV, Rechenzentrum Projektmanagement, MEP-Koordination, Cx Commissioning, Level 5 Commissioning, White Space, Grey Space, USV, Dieselgenerator, Kühlanlage, Rechenzentrum Dänemark, Edge-Rechenzentrum",
    ogTitle: "Rechenzentren & Hyperscale | MI Support",
    ogDescription:
      "Erfahrene Bauherrenberatung und Projektmanagement für betriebskritischen Rechenzentrumsbau – von Design und Ausführung bis zu Commissioning und Übergabe.",
    jsonLd: {
      serviceType: "Bauherrenberatung und Projektmanagement für Rechenzentrumsbau",
      name: "Rechenzentrums- & Hyperscale-Bau",
      description:
        "Spezialisiertes Bau-, Projekt- und HSE-Management für betriebskritische Rechenzentren, Hyperscale-Bauten und Colocation-Einrichtungen.",
      areaServed: "Dänemark",
      audienceType: "Hyperscaler, Colocation, Edge-Anbieter, Enterprise-IT",
    },
    hero: {
      eyebrow: "Branche · Rechenzentren",
      title: "Bauherrenberatung für Rechenzentren",
      body: "Mit MI Support als Berater erhalten Sie einen erfahrenen Partner während des gesamten Rechenzentrumsprojekts. Wir übernehmen die Verantwortung dafür, dass Strom, Kühlung und Technik zusammenspielen, dass die Anlage vor der Inbetriebnahme vollständig getestet ist und dass der Betrieb mit gutem Gewissen übernommen werden kann.",
      ctaPrimary: "Unverbindliches Gespräch führen",
      ctaSecondary: "Alle Branchen ansehen",
    },
    intro: {
      eyebrow: "Rechenzentrumsbau erfordert Spezialisten",
      heading: "Wenn Betriebsstabilität, Technik und Tests das Projekt bestimmen",
      p1: "In einem Rechenzentrum ist Betriebsstabilität das Geschäftsmodell selbst. Die technischen Installationen wie Stromversorgung, Kühlung, Netzwerk und Steuerungssysteme machen den Großteil des Budgets aus, und sie müssen nicht nur installiert, sondern auch gründlich im Zusammenspiel getestet werden, bevor der Betrieb übernimmt. Mit einem Projektleiter von MI Support erhalten Sie genau bei dieser Koordination einen festen Griff, sodass Sie Entscheidungen auf solider Grundlage treffen können.",
      p2: "Unsere Berater arbeiten seit mehreren Jahren an Rechenzentrumsprojekten. Das gibt uns spezialisiertes Wissen darüber, was nötig ist, wenn Techniker, Auftragnehmer und Betriebsleute unter Zeitdruck am selben Strang ziehen müssen.",
    },
    expertiseSection: { eyebrow: "Unsere Expertise", heading: "Spezialisiertes Wissen über Rechenzentrumsbau" },
    expertise: [
      { title: "Betriebssichere Bauten", text: "Wir bauen so, dass es funktioniert. Wir konzentrieren uns auf Backup-Lösungen, Ausfallsicherung und stabilen Betrieb sowohl während als auch nach dem Bau." },
      { title: "Strom & Notstrom", text: "Elektroinstallationen, Notstromanlagen, Generatoren und Transformatoren, koordiniert mit Energieversorgern und Behörden." },
      { title: "Kühlung & Lüftung", text: "Wir sorgen für das richtige Kühlsystem, sodass der Stromverbrauch niedrig gehalten wird und die Anlage im dänischen Klima optimal läuft." },
      { title: "Gründliche Tests & Kontrolle", text: "Wir testen alle Systeme gründlich unterwegs und bei der Übergabe, von einzelnen Maschinen bis zum gesamten Betrieb der Anlage." },
      { title: "Koordination der Technik", text: "Enge Zusammenarbeit zwischen Strom, Kühlung, Steuerung und IT. Wir nutzen digitale Modelle, um Fehler zu vermeiden und die Installationen zu verfolgen." },
      { title: "Höchste Betriebsklassen", text: "Erfahrung mit den strengsten Anforderungen an die Betriebssicherheit – sowohl von internationalen Standards als auch von großen globalen Tech-Unternehmen." },
    ],
    servicesSection: {
      eyebrow: "Unsere Leistungen für Rechenzentren",
      heading: "Das liefern wir Rechenzentrums-Bauherren",
      intro:
        "Ob Sie neu von Grund auf bauen, Kapazität erweitern oder eine laufende Anlage aufrüsten – unsere Berater stehen bereit, das Projekt gemeinsam mit Ihnen zu leiten und zu koordinieren.",
      readMore: "Mehr erfahren",
    },
    services: [
      { title: "Bauherrenberatung", text: "Unabhängiger Berater während des gesamten Projekts, von der Grundstückswahl und dem Design bis zu Test, Übergabe und Betrieb." },
      { title: "Projektleitung", text: "Erfahrene Projektleiter, die dafür sorgen, dass der Bau termingerecht, im Budget und in der richtigen Qualität fertig wird." },
      { title: "Baumanagement", text: "Bauleiter, die technisch anspruchsvolle Projekte, schnelle Terminpläne und das Zusammenspiel von Gebäude und Maschinen verstehen." },
      { title: "Terminplanung", text: "Übergeordnete Terminpläne, in denen Probeläufe, Lieferungen und Stromanschluss in den kritischen Pfad eingebaut sind." },
      { title: "Projektökonomie", text: "Überblick über Kosten und Budget bei Projekten mit hochmoderner Ausrüstung und oft wechselnden Anforderungen." },
      { title: "Projektassistenz", text: "Steuerung von Dokumenten und Koordination der vielen Lieferanten, Auftragnehmer und Partner." },
      { title: "Shutdown-Planung", text: "Planung von Anschlüssen und Updates an laufenden Rechenzentren mit absolutem Fokus darauf, Ausfallzeiten zu vermeiden." },
      { title: "Sicherheitsmanagement (HSE)", text: "Sicherheitsarbeit auf Rechenzentrums-Baustellen mit vielen Elektroinstallationen, Kranen und Überschneidungen der Arbeiten." },
    ],
    projectTypesSection: {
      eyebrow: "Anlagentypen",
      heading: "Erfahrung aus der gesamten Rechenzentrumsbranche",
      intro:
        "Wir arbeiten sowohl mit Tech-Unternehmen, Vermietern als auch mit Unternehmen mit eigenen Serverkapazitäten zusammen und kennen die Bedürfnisse von beiden Seiten des Tisches.",
    },
    projectTypes: [
      "Eigene Serverräume von Unternehmen",
      "Kleinere Edge-Rechenzentren",
      "Serverhallen und Geräteräume",
      "Notstrom- und Generatoranlagen",
      "Kühl- und Lüftungsanlagen",
      "Erweiterungen und Aufrüstungen",
    ],
    why: {
      eyebrow: "Warum MI Support",
      heading: "Ein Partner, der versteht, was nötig ist",
      cards: [
        { title: "Spezialisierte Berater", text: "Sie erhalten Zugang zu Beratern, die sowohl aus der Baubranche als auch aus den Technik- und Betriebsabteilungen großer internationaler Unternehmen kommen. Das bedeutet, dass wir dieselbe Sprache sprechen wie Handwerker und IT-Spezialisten und alle Parteien um das Projekt herum zusammenhalten können." },
        { title: "Unabhängiger Bauherrenberater", text: "Wir wahren ausschließlich die Interessen des Bauherrn. Ohne Bindungen an Auftragnehmer oder Lieferanten können wir die kritischen Fragen stellen, wenn es darauf ankommt, und Ihnen die volle Kontrolle über das Projekt sichern." },
        { title: "Tests, die halten", text: "Bei uns sind Tests und Commissioning kein Abschluss, sondern ein roter Faden durch das gesamte Projekt. Wir bauen die Struktur von Anfang an auf, sodass Sie die Anlage planmäßig und ohne unangenehme Überraschungen an den Betrieb übergeben können." },
      ],
    },
    cta: {
      heading: "Sollen wir bei Ihrem Rechenzentrumsprojekt helfen?",
      body: "Stehen Sie vor einem Rechenzentrumsprojekt, bei dem Betriebsstabilität, Technik und Zeit Hand in Hand gehen müssen? Wir führen gern ein unverbindliches Gespräch über Ihr Projekt und Ihre Bedürfnisse.",
      cta: "Kontaktieren Sie uns für mehr Informationen",
    },
  },
};
